import React, { memo } from 'react'
import { Handle, Position, type NodeProps } from 'reactflow'
import { Envelope, CursorClick, ChatText } from '@phosphor-icons/react'
import { TONE_STYLES, nodeCardClass, nodeChipClass, nodeHandleClass, nodeIcon, nodeTextClass, toneHandleClass } from '../../nodeCatalog'

const TYPE = 'delay'
const Icon = nodeIcon(TYPE)

const EVENT_ICONS: Record<string, React.ReactNode> = {
  email_opened: <Envelope className="w-3 h-3" />,
  email_clicked: <CursorClick className="w-3 h-3" />,
  email_replied: <ChatText className="w-3 h-3" />,
}

const EVENT_LABELS: Record<string, string> = {
  email_opened: 'Opened',
  email_clicked: 'Clicked',
  email_replied: 'Replied',
}

const DelayNode: React.FC<NodeProps> = ({ data, selected }) => {
  const isEventMode = data.delay_mode === 'wait_for_event'

  const getSubtext = () => {
    if (isEventMode) {
      const event = EVENT_LABELS[data.event_type] || 'Event'
      const timeout = data.event_timeout_value || 3
      const unit = data.event_timeout_unit || 'days'
      return `${event} within ${timeout} ${unit}`
    }
    if (data.delay_mode === 'wait_until_time') {
      return `Until ${data.wait_until_time || '09:00'}`
    }
    return data.delay_value && data.delay_unit
      ? `${data.delay_value} ${data.delay_unit}`
      : 'Not configured'
  }

  return (
    <div className={nodeCardClass(TYPE, selected)}>
      <Handle
        type="target"
        position={Position.Top}
        className={nodeHandleClass(TYPE)}
      />
      <div className="flex items-center gap-2">
        <div className={nodeChipClass(TYPE)}>
          <Icon className={`w-4 h-4 ${nodeTextClass(TYPE)}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-emphasis uppercase tracking-wider ${nodeTextClass(TYPE)}`}>
            {isEventMode ? 'Wait for Event' : 'Delay'}
          </p>
          <p className="text-sm font-medium text-foreground truncate">{data.label || 'Wait'}</p>
        </div>
      </div>
      <div className="mt-1 pl-10 flex items-center gap-1.5 text-xs text-muted-foreground">
        {isEventMode && EVENT_ICONS[data.event_type]}
        <span>{getSubtext()}</span>
      </div>

      {isEventMode ? (
        <>
          {/* The two outlets carry their own meaning, not this node's tone: the event
              fired (success) vs. the wait expired without it (time). */}
          <Handle
            type="source"
            position={Position.Bottom}
            id="event"
            style={{ left: '35%' }}
            className={toneHandleClass('success')}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="timeout"
            style={{ left: '65%' }}
            className={toneHandleClass('time')}
          />
          {/* Labels */}
          <div className="flex justify-between mt-1.5 px-1">
            <span className={`text-[9px] font-medium ${TONE_STYLES.success.text}`}>Event</span>
            <span className={`text-[9px] font-medium ${TONE_STYLES.time.text}`}>Timeout</span>
          </div>
        </>
      ) : (
        <Handle
          type="source"
          position={Position.Bottom}
          className={nodeHandleClass(TYPE)}
        />
      )}
    </div>
  )
}

export default memo(DelayNode)
